'use client';

import { ApolloError, useApolloClient } from '@apollo/client';
import { KeyRound, ShieldCheck, Smartphone, X } from 'lucide-react';
import { type FormEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import SfLogoMain from '@/assets/icons/sidebar-nav/sportsfi-logo-main';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { SECURITY_STATUS_GET_CURRENT_USER } from '@/graphql/security';
import {
  SENSITIVE_ACTION_CONFIRMATION_CREATE,
  SENSITIVE_ACTION_PASSKEY_CHALLENGE_CREATE,
  SENSITIVE_ACTION_PASSKEY_CONFIRMATION_VERIFY,
  SENSITIVE_ACTION_POLICY_GET,
} from '@/graphql/sensitive-actions';
import {
  SensitiveActionConfirmationCancelledError,
  beginSensitiveActionConfirmationPopup,
  setSensitiveActionConfirmationRequester,
  type SensitiveActionConfirmationRequest,
  type SensitiveActionConfirmationRequesterResult,
  type SensitiveActionConfirmationResult,
} from '@/lib/auth/sensitiveActionFlow';
import {
  formatSensitiveActionPrompt,
  formatSensitiveActionReason,
  formatSensitiveActionRequiredLevelLabel,
} from '@/lib/auth/sensitiveActionDisplay';

type PendingSensitiveActionConfirmation = {
  request: SensitiveActionConfirmationRequest;
  resolve: (result: SensitiveActionConfirmationRequesterResult) => void;
  reject: (reason: unknown) => void;
};

type NativeMode = 'loading' | 'passkey' | 'totp' | 'fallback';

type SensitiveActionConfirmationProviderProps = {
  children: ReactNode;
};

type AccountSecurityStatus = {
  hasTotp: boolean;
  hasPasskey: boolean;
  totpDevices: { credentialId: string; label: string }[];
  passkeys: { credentialId: string; label: string }[];
  recoveryCodesConfigured: boolean;
};

type SensitiveActionPolicy = {
  actionId: string;
  requiredLevel: 'password' | 'mfa' | 'admin_mfa' | 'passkey_mfa';
  reason?: string | null;
  isBlocked: boolean;
};

type PasskeyChallenge = {
  challengeId: string;
  publicKey: {
    challenge: string;
    rpId: string;
    timeout: number;
    userVerification: UserVerificationRequirement;
    allowCredentials: { id: string; type: 'public-key'; transports?: AuthenticatorTransport[] }[];
  };
};

const getActionPrompt = formatSensitiveActionPrompt;

const getActionReason = (
  request: SensitiveActionConfirmationRequest,
  policy: SensitiveActionPolicy | null
): string | null => formatSensitiveActionReason(request.actionId, policy?.reason ?? request.reason);

const getRequiredLevelLabel = formatSensitiveActionRequiredLevelLabel;

const base64UrlToArrayBuffer = (value: string): ArrayBuffer => {
  const padded = `${value.replaceAll('-', '+').replaceAll('_', '/')}${'='.repeat((4 - (value.length % 4)) % 4)}`;
  const binary = window.atob(padded);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes.buffer;
};

const arrayBufferToBase64Url = (value: ArrayBuffer): string => {
  const bytes = new Uint8Array(value);
  let binary = '';

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return window.btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
};

const toPublicKeyCredentialRequestOptions = (
  publicKey: PasskeyChallenge['publicKey']
): PublicKeyCredentialRequestOptions => ({
  challenge: base64UrlToArrayBuffer(publicKey.challenge),
  rpId: publicKey.rpId,
  timeout: publicKey.timeout,
  userVerification: publicKey.userVerification,
  allowCredentials: publicKey.allowCredentials.map((credential) => {
    const transports = credential.transports?.length === 0 ? undefined : credential.transports;

    return {
      id: base64UrlToArrayBuffer(credential.id),
      type: 'public-key',
      ...(transports != null ? { transports } : {}),
    };
  }),
});

type VerificationMethodTileProps = {
  description: string;
  disabled: boolean;
  icon: ReactNode;
  isActive?: boolean;
  isRecommended?: boolean;
  isVerifying?: boolean;
  label: string;
  onClick: () => void;
};

const VerificationMethodTile = ({
  description,
  disabled,
  icon,
  isActive = false,
  isRecommended = false,
  isVerifying = false,
  label,
  onClick,
}: VerificationMethodTileProps) => (
  <button
    type="button"
    className={[
      'group flex min-h-[116px] w-full items-center gap-4 rounded-lg border p-4 text-left transition',
      isActive ? 'border-cyan-300/70 bg-cyan-300/12' : 'border-white/15 bg-white/[0.06]',
      'hover:border-cyan-300/60 hover:bg-cyan-300/10 disabled:cursor-wait disabled:opacity-70',
    ].join(' ')}
    onClick={onClick}
    disabled={disabled}
  >
    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-cyan-300/15 text-cyan-300">
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex flex-wrap items-center gap-2 text-lg font-bold text-white">
        {label}
        {isRecommended ? (
          <span className="rounded border border-cyan-300/50 px-2 py-0.5 text-[10px] font-bold uppercase text-cyan-200">
            Recommended
          </span>
        ) : null}
      </span>
      <span className="mt-1 block text-sm leading-6 text-slate-300">{description}</span>
    </span>
    {isVerifying ? <LoadingSpinner size={20} label={`Verifying ${label}`} /> : null}
  </button>
);

const toWebAuthnAssertionInput = (credential: PublicKeyCredential) => {
  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
      signature: arrayBufferToBase64Url(response.signature),
      userHandle: response.userHandle == null ? null : arrayBufferToBase64Url(response.userHandle),
    },
  };
};

const getGraphQLErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApolloError && error.graphQLErrors[0]?.message != null) {
    return error.graphQLErrors[0].message;
  }

  return error instanceof Error ? error.message : fallback;
};

export function SensitiveActionConfirmationProvider({ children }: SensitiveActionConfirmationProviderProps): ReactNode {
  const apolloClient = useApolloClient();
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingSensitiveActionConfirmation | null>(null);
  const [mode, setMode] = useState<NativeMode>('loading');
  const [policy, setPolicy] = useState<SensitiveActionPolicy | null>(null);
  const [securityStatus, setSecurityStatus] = useState<AccountSecurityStatus | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pendingRef = useRef<PendingSensitiveActionConfirmation | null>(null);

  const clearPending = useCallback(() => {
    pendingRef.current = null;
    setPendingConfirmation(null);
    setMode('loading');
    setPolicy(null);
    setSecurityStatus(null);
    setTotpCode('');
    setIsVerifying(false);
    setErrorMessage(null);
  }, []);

  const buildInput = useCallback(
    (request: SensitiveActionConfirmationRequest) => ({
      actionId: request.actionId,
      targetResourceType: request.targetResourceType ?? null,
      targetResourceId: request.targetResourceId ?? null,
      operationKey: request.operationKey,
      metadata: request.metadata ?? null,
    }),
    []
  );

  const finishWithResult = useCallback(
    (result: SensitiveActionConfirmationResult) => {
      const pending = pendingRef.current;

      if (pending == null) {
        return;
      }

      pending.resolve({ status: 'confirmed', result });
      clearPending();
    },
    [clearPending]
  );

  const loadNativeState = useCallback(
    async (pending: PendingSensitiveActionConfirmation) => {
      setMode('loading');
      setErrorMessage(null);

      try {
        const [policyResult, statusResult] = await Promise.all([
          apolloClient.query<{ sensitiveActionPolicyGet: SensitiveActionPolicy }>({
            query: SENSITIVE_ACTION_POLICY_GET,
            variables: {
              actionId: pending.request.actionId,
              target: {
                targetResourceType: pending.request.targetResourceType ?? null,
                targetResourceId: pending.request.targetResourceId ?? null,
              },
            },
            fetchPolicy: 'network-only',
          }),
          apolloClient.query<{ securityStatusGetCurrentUser: AccountSecurityStatus }>({
            query: SECURITY_STATUS_GET_CURRENT_USER,
            fetchPolicy: 'network-only',
          }),
        ]);
        const resolvedPolicy = policyResult.data.sensitiveActionPolicyGet;
        const resolvedStatus = statusResult.data.securityStatusGetCurrentUser;

        setPolicy(resolvedPolicy);
        setSecurityStatus(resolvedStatus);

        if (resolvedPolicy.isBlocked) {
          setMode('fallback');
          setErrorMessage(resolvedPolicy.reason ?? 'This action cannot be confirmed with the current account.');

          return;
        }

        if (resolvedPolicy.requiredLevel === 'passkey_mfa') {
          setMode(resolvedStatus.hasPasskey ? 'passkey' : 'fallback');

          return;
        }

        if (resolvedStatus.hasPasskey) {
          setMode('passkey');

          return;
        }

        if (resolvedStatus.hasTotp) {
          setMode('totp');

          return;
        }

        setMode('fallback');
        setErrorMessage('Set up an authenticator app or passkey before continuing.');
      } catch (error) {
        setMode('fallback');
        setErrorMessage(getGraphQLErrorMessage(error, 'Security confirmation could not be prepared.'));
      }
    },
    [apolloClient]
  );

  const requestConfirmation = useCallback(
    async (request: SensitiveActionConfirmationRequest): Promise<SensitiveActionConfirmationRequesterResult> => {
      if (pendingRef.current != null) {
        throw new Error('Finish the current security confirmation first.');
      }

      return await new Promise<SensitiveActionConfirmationRequesterResult>((resolve, reject) => {
        const pending = { reject, request, resolve };

        pendingRef.current = pending;
        setPendingConfirmation(pending);
        void loadNativeState(pending);
      });
    },
    [loadNativeState]
  );

  useEffect(() => setSensitiveActionConfirmationRequester(requestConfirmation), [requestConfirmation]);

  const cancelConfirmation = useCallback(() => {
    const pending = pendingRef.current;

    if (pending == null) {
      return;
    }

    pending.resolve({ status: 'cancelled' });
    clearPending();
  }, [clearPending]);

  const startPopupFallback = useCallback(async () => {
    const pending = pendingRef.current;

    if (pending == null) {
      return;
    }

    setIsVerifying(true);
    setErrorMessage(null);

    try {
      const result = await beginSensitiveActionConfirmationPopup(pending.request);

      pending.resolve(result != null ? { result, status: 'confirmed' } : { status: 'cancelled' });
      clearPending();
    } catch (error) {
      if (error instanceof SensitiveActionConfirmationCancelledError) {
        pending.resolve({ status: 'cancelled' });
        clearPending();

        return;
      }

      setErrorMessage(getGraphQLErrorMessage(error, 'Security confirmation could not be completed.'));
      setIsVerifying(false);
    }
  }, [clearPending]);

  const verifyTotp = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      event?.preventDefault();

      const pending = pendingRef.current;
      const code = totpCode.replaceAll(/\s/gu, '');

      if (pending == null || code.length === 0) {
        return;
      }

      setIsVerifying(true);
      setErrorMessage(null);

      try {
        const result = await apolloClient.mutate<{
          sensitiveActionConfirmationCreate: SensitiveActionConfirmationResult;
        }>({
          mutation: SENSITIVE_ACTION_CONFIRMATION_CREATE,
          variables: {
            input: {
              ...buildInput(pending.request),
              totpCode: code,
            },
          },
        });
        const confirmation = result.data?.sensitiveActionConfirmationCreate;

        if (confirmation == null) {
          throw new Error('Security confirmation did not return a token.');
        }

        finishWithResult(confirmation);
      } catch (error) {
        setErrorMessage(getGraphQLErrorMessage(error, 'Authenticator code was not accepted.'));
        setIsVerifying(false);
      }
    },
    [apolloClient, buildInput, finishWithResult, totpCode]
  );

  const verifyPasskey = useCallback(async () => {
    const pending = pendingRef.current;

    if (pending == null) {
      return;
    }

    if (typeof window === 'undefined' || window.PublicKeyCredential == null || navigator.credentials == null) {
      setMode(securityStatus?.hasTotp === true ? 'totp' : 'fallback');
      setErrorMessage('This browser cannot use passkeys here. Use another authentication method.');

      return;
    }

    setIsVerifying(true);
    setErrorMessage(null);

    try {
      const challengeResult = await apolloClient.mutate<{ sensitiveActionPasskeyChallengeCreate: PasskeyChallenge }>({
        mutation: SENSITIVE_ACTION_PASSKEY_CHALLENGE_CREATE,
        variables: { input: buildInput(pending.request) },
      });
      const challenge = challengeResult.data?.sensitiveActionPasskeyChallengeCreate;

      if (challenge == null) {
        throw new Error('Passkey challenge could not be created.');
      }

      const credential = await navigator.credentials.get({
        publicKey: toPublicKeyCredentialRequestOptions(challenge.publicKey),
      });

      if (!(credential instanceof PublicKeyCredential)) {
        throw new SensitiveActionConfirmationCancelledError();
      }

      const verificationResult = await apolloClient.mutate<{
        sensitiveActionPasskeyConfirmationVerify: SensitiveActionConfirmationResult;
      }>({
        mutation: SENSITIVE_ACTION_PASSKEY_CONFIRMATION_VERIFY,
        variables: {
          input: {
            ...buildInput(pending.request),
            challengeId: challenge.challengeId,
            assertion: toWebAuthnAssertionInput(credential),
          },
        },
      });
      const confirmation = verificationResult.data?.sensitiveActionPasskeyConfirmationVerify;

      if (confirmation == null) {
        throw new Error('Passkey confirmation did not return a token.');
      }

      finishWithResult(confirmation);
    } catch (error) {
      if (error instanceof SensitiveActionConfirmationCancelledError) {
        setErrorMessage('Passkey verification was cancelled.');
      } else {
        setErrorMessage(getGraphQLErrorMessage(error, 'Passkey verification failed.'));
      }

      setIsVerifying(false);
    }
  }, [apolloClient, buildInput, finishWithResult, securityStatus?.hasTotp]);

  const request = pendingConfirmation?.request ?? null;
  const canUsePasskey = securityStatus?.hasPasskey === true;
  const canUseTotp = securityStatus?.hasTotp === true && policy?.requiredLevel !== 'passkey_mfa';
  const showMethodTiles = canUsePasskey && canUseTotp;
  const reason = request == null ? null : getActionReason(request, policy);

  return (
    <>
      {children}

      {request != null ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-sm">
          <section className="relative w-full max-w-[640px] overflow-hidden rounded-[28px] border border-cyan-300/25 bg-[#07131d]/95 text-white shadow-2xl">
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-cyan-400/15 to-transparent" />
            <button
              type="button"
              className="absolute right-5 top-5 z-20 rounded-md p-2 text-white/70 transition hover:bg-white/10 hover:text-white"
              onClick={cancelConfirmation}
              aria-label="Cancel security confirmation"
            >
              <X className="h-6 w-6" />
            </button>

            <div className="relative p-8 sm:p-10">
              <div className="mb-7 flex justify-center">
                <SfLogoMain className="sportsfi-login-logo h-auto w-[230px]" />
              </div>

              <div className="mb-6 text-center">
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">
                  {getRequiredLevelLabel(policy?.requiredLevel ?? request.requiredLevel)}
                </p>
                <h2 className="text-3xl font-bold tracking-normal text-white">Step-up verification</h2>
                <p className="mx-auto mt-3 max-w-[430px] text-base leading-7 text-slate-300">
                  {getActionPrompt(request.actionId)}
                </p>
              </div>

              {reason != null ? (
                <div className="mb-5 rounded-lg border border-cyan-300/20 bg-white/[0.06] px-4 py-3 text-sm leading-6 text-slate-200">
                  {reason}
                </div>
              ) : null}

              {errorMessage != null ? (
                <div className="mb-5 rounded-lg border border-red-300/40 bg-red-500/15 px-4 py-3 text-sm font-semibold text-red-100">
                  {errorMessage}
                </div>
              ) : null}

              {mode === 'loading' ? (
                <div className="flex min-h-[180px] items-center justify-center">
                  <LoadingSpinner size={36} label="Loading verification methods" />
                </div>
              ) : null}

              {mode === 'passkey' || mode === 'totp' ? (
                <div className="space-y-4">
                  {canUsePasskey ? (
                    <VerificationMethodTile
                      description="Use a password-manager passkey, Touch ID, Windows Hello, or a security key."
                      disabled={isVerifying}
                      icon={<KeyRound className="h-6 w-6" />}
                      isActive={mode === 'passkey'}
                      isRecommended
                      isVerifying={mode === 'passkey' && isVerifying}
                      label="Use passkey"
                      onClick={verifyPasskey}
                    />
                  ) : null}

                  {showMethodTiles && canUseTotp ? (
                    <VerificationMethodTile
                      description="Enter the current 6-digit code from your authenticator app."
                      disabled={isVerifying}
                      icon={<Smartphone className="h-6 w-6" />}
                      isActive={mode === 'totp'}
                      label="Use authenticator code"
                      onClick={() => setMode('totp')}
                    />
                  ) : null}

                  {mode === 'totp' ? (
                    <form className="space-y-4" onSubmit={verifyTotp}>
                      <label className="block text-sm font-semibold text-slate-100" htmlFor="sensitive-action-totp">
                        Authenticator code
                      </label>
                      <input
                        id="sensitive-action-totp"
                        name="one-time-code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="[0-9]*"
                        value={totpCode}
                        onChange={(event) => setTotpCode(event.target.value)}
                        className="h-14 w-full rounded-lg border border-white/10 bg-white px-4 text-center text-2xl font-bold tracking-[0.3em] text-slate-950 outline-none ring-cyan-300/40 transition focus:ring-4"
                        maxLength={8}
                        disabled={isVerifying}
                      />
                      <button
                        type="submit"
                        className="flex h-14 w-full items-center justify-center rounded-lg bg-cyan-500 text-sm font-bold text-white transition hover:bg-cyan-400 disabled:cursor-wait disabled:opacity-70"
                        disabled={isVerifying || totpCode.trim().length === 0}
                      >
                        {isVerifying ? <LoadingSpinner size={20} label="Confirming authenticator code" /> : 'Confirm'}
                      </button>
                    </form>
                  ) : null}
                </div>
              ) : null}

              {mode === 'fallback' ? (
                <div className="space-y-4">
                  <button
                    type="button"
                    className="flex h-14 w-full items-center justify-center rounded-lg bg-cyan-500 text-sm font-bold text-white transition hover:bg-cyan-400 disabled:cursor-wait disabled:opacity-70"
                    onClick={startPopupFallback}
                    disabled={isVerifying}
                  >
                    {isVerifying ? (
                      <LoadingSpinner size={20} label="Opening sign-in verification" />
                    ) : (
                      'Verify in sign-in window'
                    )}
                  </button>
                </div>
              ) : null}

              <div className="mt-8 flex items-center justify-between text-sm text-slate-300">
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-cyan-300" />
                  Secure connection
                </span>
                <button
                  type="button"
                  className="font-semibold text-cyan-200 hover:text-cyan-100"
                  onClick={cancelConfirmation}
                >
                  Cancel
                </button>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
